import { DEFAULT_GUIDED_DRAFT } from '../../core/guided'
import { priceForDraft, unitRates } from '../../core/rateCard'
import { formatSize } from '../../core/sizing'
import type { UnitPricesDto } from '../../data/types'
import type { CatalogQuery } from './catalogQuery'

/**
 * One comparable price per host, for the catalog's price sort.
 *
 * A price list is per resource, so "cheapest host" has no answer until a shape
 * is named: a host with cheap CPU and dear memory is the cheaper one for a
 * compute job and the dearer one for a cache. The sort therefore prices a
 * named shape — the guided form's default, or the tenant's own At-least values
 * — and the sort chip says which one, so the ranking is never an unlabelled
 * "cheapest".
 *
 * GPU is not part of the shape: the guided form cannot request one.
 */
export interface PriceShape {
  /** Whole millicores. */
  cpuMilli: number
  /** Whole GiB. */
  memoryGiB: number
  /** Whole GiB. */
  storageGiB: number
}

/**
 * The shape the price sort ranks by.
 *
 * An At-least value is rounded UP to the unit the rate card is quoted in
 * (millicores, whole GiB), so the arithmetic stays in integers and the shape
 * still satisfies the floor the tenant typed. The chip names the rounded
 * shape, so what is ranked and what is said are the same thing.
 */
export function priceShapeFor(query: Pick<CatalogQuery, 'minCpu' | 'minRam'>): PriceShape {
  const cores = Number(query.minCpu)
  const ram = Number(query.minRam)
  return {
    cpuMilli: query.minCpu && cores > 0 ? Math.ceil(cores * 1000) : DEFAULT_GUIDED_DRAFT.cpu,
    memoryGiB: query.minRam && ram > 0 ? Math.ceil(ram) : DEFAULT_GUIDED_DRAFT.ram,
    storageGiB: DEFAULT_GUIDED_DRAFT.storage,
  }
}

/** "1 core · 2 GB · 10 GB", in the words the guided form's size tiles use. */
export function priceShapeLabel(shape: PriceShape): string {
  return formatSize({ cpu: shape.cpuMilli, ram: shape.memoryGiB, storage: shape.storageGiB })
}

/**
 * Exact microdollars per hour for `shape` on this rate card, or `null`.
 *
 * Priced through `core/rateCard`, the console's mirror of the provider's own
 * `PriceForCapacity`, so the ranking cannot drift from the price the configure
 * page shows for the same host and shape.
 *
 * `null` is unknown, never cheap: a missing card, a missing or unreadable
 * component, a foreign denomination or a block cadence that does not divide
 * an hour all return it, and the sort places unknowns last in both
 * directions. Every component must be readable — pricing a shape from two of
 * its three parts would rank a host by a figure it never quoted.
 */
export function shapePriceMicrosPerHour(prices: UnitPricesDto | null | undefined, shape: PriceShape): bigint | null {
  const rates = unitRates(prices)
  if (!rates) return null
  // What the lease would charge per hour: the exact sum rounded up to whole
  // uddt per block, as the provider rounds it.
  return priceForDraft(rates, { cpu: shape.cpuMilli, ram: shape.memoryGiB, storage: shape.storageGiB }, 1)?.totalPerHour ?? null
}
